import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Loader2, Eye, EyeOff, CheckCircle, Key } from 'lucide-react';
import { LanguageSelector } from '../../components/ui/language-selector';
import { useTranslation } from 'react-i18next';
import { changeLanguage, type SupportedLanguage } from '../../lib/language-service';
import { supabase } from '@/lib/supabaseClient';
import { authLogger } from '@/lib/logging';

const PasswordResetPage: React.FC = () => {
  // console.warn('PasswordResetPage: Custom token-based password reset. URL:', window.location.href);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tokenValidated, setTokenValidated] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>('en');
  const [userEmail, setUserEmail] = useState<string>('');

  // Get token and language from URL query params
  const token = searchParams.get('token');
  const urlLanguage = searchParams.get('lng') as SupportedLanguage;

  // Initialize language from URL parameter
  useEffect(() => {
    if (urlLanguage) {
      // console.warn('PasswordResetPage: Setting language from URL:', urlLanguage);
      setSelectedLanguage(urlLanguage);
      i18n.changeLanguage(urlLanguage);
    }
  }, [urlLanguage, i18n]);

  const handleLanguageChange = async (language: SupportedLanguage) => {
    // console.warn('PasswordResetPage: Language changed to:', language);
    setSelectedLanguage(language);
    await i18n.changeLanguage(language);

    // Save to cookie/localStorage for persistence
    await changeLanguage(language, false); // Don't save to database since user isn't authenticated yet
  };

  useEffect(() => {
    // console.warn('PasswordResetPage: Validating custom token:', token);

    if (!token) {
      authLogger.error('PasswordResetPage: No token found in URL');
      setError(t('passwordReset.errors.noToken'));
      setValidatingToken(false);
      return;
    }

    // Validate the token with our custom endpoint
    const validateToken = async () => {
      try {
        const response = await fetch(`${import.meta.env['VITE_PUBLIC_SUPABASE_URL']}/functions/v1/validate-password-reset`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env['VITE_PUBLIC_SUPABASE_ANON_KEY']}`
          },
          body: JSON.stringify({
            token: token
            // Not providing newPassword - just validating the token
          })
        });

        const data = await response.json();

        // console.warn('PasswordResetPage: Token validation response:', {
        //   success: response.ok,
        //   status: response.status,
        //   message: data.message,
        //   error: data.error
        // });

        setValidatingToken(false);

        if (response.ok) {
          setTokenValidated(true);
          setUserEmail(data.userEmail || '');
          setMessage(t('passwordReset.tokenValid'));
        } else {
          setError(data.error || t('passwordReset.errors.invalidToken'));
        }
      } catch (_error) {
        authLogger.error('Failed operation:', _error);
        setValidatingToken(false);
        setError(t('passwordReset.errors.validationError'));
      }
    };

    validateToken();
  }, [token, t]);

  const handlePasswordUpdate = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!token || !tokenValidated) {
      setError(t('passwordReset.errors.invalidToken'));
      return;
    }

    if (!newPassword) {
      setError(t('passwordReset.errors.enterPassword'));
      return;
    }

    setError(null);
    setMessage(null);
    setLoading(true);

    // console.warn('PasswordResetPage: Updating password with custom token');

    try {
      const response = await fetch(`${import.meta.env['VITE_PUBLIC_SUPABASE_URL']}/functions/v1/validate-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env['VITE_PUBLIC_SUPABASE_ANON_KEY']}`
        },
        body: JSON.stringify({
          token: token,
          newPassword: newPassword,
          language: selectedLanguage
        })
      });

      const data = await response.json();

      // console.warn('PasswordResetPage: Password update response:', {
      //   success: response.ok,
      //   status: response.status,
      //   message: data.message,
      //   error: data.error
      // });

      setLoading(false);

      if (response.ok) {
        authLogger.warn('Password reset successful:', data);

        // Set success message
        setMessage(t('passwordReset.success'));

        // Auto-login the user with their new password
        try {
          const { data: _loginData, error: loginError } = await supabase.auth.signInWithPassword({
            email: data.userEmail || userEmail,
            password: newPassword,
          });

          if (loginError) {
            authLogger.error('Auto-login failed:', loginError);
            // If auto-login fails, redirect to login page
            setMessage(t('passwordReset.successRedirectLogin'));
            setTimeout(() => {
              navigate('/login');
            }, 2000);
          } else {
            authLogger.warn('Auto-login successful, navigating to projects...');
            setMessage(t('passwordReset.successRedirectDashboard'));
            setTimeout(() => {
              navigate('/projects');
            }, 1500);
          }
        } catch (loginError) {
          authLogger.error('Auto-login error:', loginError);
          // If auto-login fails, redirect to login page
          setMessage(t('passwordReset.successRedirectLogin'));
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        }
      } else {
        setError(data.error || t('passwordReset.errors.updateFailed'));
      }
    } catch (_error) {
      authLogger.error('Failed operation:', _error);
      setLoading(false);
      setError(t('passwordReset.errors.updateError'));
    }
  };

  return (
    <div 
      className='flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 transition-colors duration-500' 
      style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        width: '100%',
        position: 'fixed',
        top: 0,
        left: 0
      }}
    >
      <div className='w-full max-w-md'>
        <Card className='shadow-xl border-0 bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-2xl'>
          <CardHeader className='text-center pb-8'>
            <div className='flex justify-end mb-4'>
              <LanguageSelector
                value={selectedLanguage}
                onLanguageChange={handleLanguageChange}
                variant="select"
                size="sm"
                showIcon={true}
              />
            </div>
            <CardTitle className='text-3xl font-bold text-slate-900'>
              {t('passwordReset.title')}
            </CardTitle>
            <CardDescription className='text-slate-600 mt-2'>
              {t('passwordReset.description')}
            </CardDescription>
          </CardHeader>
          <CardContent className='pb-8'>
            {!validatingToken ? (
              tokenValidated ? (
                <form onSubmit={handlePasswordUpdate} className='space-y-6'>
                  <div className='space-y-2'>
                    <Label htmlFor='newPassword' className='text-sm font-medium text-slate-700'>
                      {t('passwordReset.newPassword')}
                    </Label>
                    <div className='relative'>
                      <Input
                        id='newPassword'
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t('passwordReset.passwordPlaceholder')}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        disabled={loading}
                        className='h-12 pr-12 transition-all duration-200 focus:ring-2 focus:ring-primary/20 border-slate-200 hover:border-primary/40'
                      />
                      <button
                        type='button'
                        onClick={() => setShowPassword(!showPassword)}
                        className='absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors'
                        disabled={loading}
                        aria-label={showPassword ? t('passwordReset.hidePassword') : t('passwordReset.showPassword')}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                  {error && (
                    <div className='p-3 bg-red-50 border border-red-200 rounded-lg'>
                      <p className='text-sm text-red-700'>{error}</p>
                    </div>
                  )}
                  {message && (
                    <div className='p-3 bg-green-50 border border-green-200 rounded-lg'>
                      <div className='flex items-start'>
                        <CheckCircle className='h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0' />
                        <p className='text-sm text-green-700'>{message}</p>
                      </div>
                    </div>
                  )}
                  <Button
                    type='submit'
                    className='w-full h-12 text-base font-medium transition-all duration-200 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]'
                    disabled={loading}
                  >
                    {loading ? (
                      <div className='flex items-center justify-center'>
                        <Loader2 className='mr-2 h-5 w-5 animate-spin' />
                        {t('passwordReset.updating')}
                      </div>
                    ) : (
                      <div className='flex items-center justify-center'>
                        <Key className='mr-2 h-5 w-5' />
                        {t('passwordReset.updateButton')}
                      </div>
                    )}
                  </Button>
                </form>
              ) : (
                // Show error state when validation failed
                <div className='space-y-6'>
                  {error && (
                    <div className='p-3 bg-red-50 border border-red-200 rounded-lg'>
                      <p className='text-sm text-red-700'>{error}</p>
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className='space-y-6'>
                <div className='flex items-center justify-center p-8'>
                  <Loader2 className='h-8 w-8 animate-spin text-primary' />
                </div>
                <div className='text-center'>
                  {error ? (
                    <div className='p-3 bg-red-50 border border-red-200 rounded-lg'>
                      <p className='text-sm text-red-700'>{error}</p>
                    </div>
                  ) : validatingToken ? (
                    <p className='text-sm text-slate-600'>{t('passwordReset.validatingToken')}</p>
                  ) : (
                    <p className='text-sm text-slate-600'>{t('passwordReset.processingLink')}</p>
                  )}
                </div>
              </div>
            )}
            <div className='mt-8 space-y-4 text-center'>
              <div className='relative'>
                <div className='absolute inset-0 flex items-center'>
                  <div className='w-full border-t border-slate-200' />
                </div>
                <div className='relative flex justify-center text-sm'>
                  <span className='px-4 bg-white text-slate-500'>{t('passwordReset.needHelp')}</span>
                </div>
              </div>
              <p className='text-sm text-slate-600'>
                <Link 
                  to='/login' 
                  className='font-medium text-primary hover:text-primary/80 transition-colors duration-200'
                >
                  {t('passwordReset.backToLogin')}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PasswordResetPage;